import * as React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

/**
 * Drop-in replacement for app-shell's `usePagination` that persists `page` and
 * `pageSize` to the URL query string instead of component state. This keeps the
 * table's position intact across reloads and browser back/forward — navigating
 * into a detail page and back restores the exact page the user was on.
 *
 * Convention (shared with {@link useTableFilters}): page 1 and the default page
 * size are represented by *absent* params, so a pristine list has a clean URL.
 * Changing a filter clears `page` (handled in `useTableFilters`), so the result
 * never lands the user on a now-empty page.
 */
export function useUrlPagination(initialPageSize = 20) {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const page = Math.max(1, Number(searchParams.get("page")) || 1);
  const pageSize = Number(searchParams.get("pageSize")) || initialPageSize;

  const write = React.useCallback(
    (mut: (sp: URLSearchParams) => void) => {
      const sp = new URLSearchParams(searchParams.toString());
      mut(sp);
      const qs = sp.toString();
      nav(qs ? `${pathname}?${qs}` : pathname, { replace: true });
    },
    [searchParams, pathname, nav],
  );

  const setPage = React.useCallback(
    (p: number) =>
      write((sp) => {
        if (p <= 1) sp.delete("page");
        else sp.set("page", String(p));
      }),
    [write],
  );

  // Changing page size resets to page 1 — the old offset is meaningless.
  const setPageSize = React.useCallback(
    (size: number) =>
      write((sp) => {
        sp.delete("page");
        if (size === initialPageSize) sp.delete("pageSize");
        else sp.set("pageSize", String(size));
      }),
    [write, initialPageSize],
  );

  const reset = React.useCallback(
    () => write((sp) => sp.delete("page")),
    [write],
  );

  return { page, pageSize, setPage, setPageSize, reset };
}
